import React, { useEffect, useRef, useState } from "react";
import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import ClaimForm from "./ClaimForm";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { detailsToFormValues, isDraftClaim } from "../claimsMappers";
import { getApiErrorMessage, loadClaimForEdit, saveClaim } from "../claimsService";
import { formatClaimMoney } from "../claimsShared";

// Content for one dynamic "claim" tab — either an existing claim (claimNumber set,
// fetched fresh by id/office so the tab never shows a stale snapshot) or a blank
// "New Claim" form (claimNumber null/undefined). Cancel/Save Draft/Submit/Save
// Changes live in the sticky header (same pattern as ShipmentDetailsView) instead
// of at the bottom of the form — ClaimForm exposes them via a ref.
const ClaimTabContent = ({ claimNumber, officeCode, access, onClose, onSaved }) => {
  // Creating and editing are the same capability today (Shipper Admin), but they are read
  // separately so a carrier can never end up with a writable form through either one.
  const canManageClaims = Boolean(access.canEditClaim ?? access.canCreateClaim);
  const isReadOnlyForm = !canManageClaims;

  const [editingClaim, setEditingClaim] = useState(null);
  const [isLoadingClaim, setIsLoadingClaim] = useState(Boolean(claimNumber));
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFieldErrors, setSaveFieldErrors] = useState(null);
  const claimFormRef = useRef(null);

  useEffect(() => {
    if (!claimNumber) return;

    let cancelled = false;
    setIsLoadingClaim(true);
    setLoadError(null);

    loadClaimForEdit(claimNumber, access.officeCode, officeCode)
      .then((loaded) => {
        if (!cancelled) setEditingClaim(loaded);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(getApiErrorMessage(error, "Failed to load claim details."));
        notify.error({
          title: "Unable to Load Claim",
          message: getApiErrorMessage(error, "Failed to load claim details."),
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClaim(false);
      });

    return () => {
      cancelled = true;
    };
  }, [claimNumber, officeCode, access.officeCode]);

  const handleSubmitClaim = async (form, { submit, draft } = {}) => {
    if (!canManageClaims) return;

    if (draft && (!form.claimType || !form.relatedStopId || !form.shipmentNumber)) {
      notify.warn({
        title: "Draft Not Saved",
        message: "Shipment, claim type, and related stop are required to save a draft.",
      });
      return;
    }

    try {
      setIsSaving(true);
      setSaveFieldErrors(null);
      await saveClaim({
        form,
        officeCode: editingClaim?.officeCode || access.officeCodes?.join(",") || access.officeCode,
        existingClaim: editingClaim,
        mode: "standalone",
        submit,
        draft,
      });

      notify.success({
        title: draft ? "Draft Saved" : submit ? "Claim Submitted" : "Claim Saved",
        message: draft
          ? "Claim draft saved."
          : submit
            ? "Claim filed successfully."
            : "Claim updated successfully.",
      });

      onSaved?.();
      onClose?.();
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to save claim.");
      // A 409 on save is the duplicate Claim Number guard — put it on the field the user
      // has to fix, not only in a toast that disappears.
      if (error?.response?.status === 409) {
        setSaveFieldErrors({ claimNumber: message });
      }
      notify.error({
        title: error?.response?.status === 409 ? "Duplicate Claim Number" : "Save Failed",
        message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isNewClaim = !claimNumber;
  const isDraftOrNew = isNewClaim || isDraftClaim(editingClaim || {});
  const actionsDisabled = isSaving || isLoadingClaim;

  // Carrier/read-only access: no Cancel/Close button at all — just the tab's own
  // close (x), since there's nothing to cancel out of on a view-only page.
  const headerButtons = isReadOnlyForm
    ? []
    : isDraftOrNew
      ? [
          { label: "Cancel", onClick: onClose, color: "gray", disabled: isSaving },
          {
            label: isSaving ? "Saving..." : "Save Draft",
            onClick: () => claimFormRef.current?.saveDraft(),
            color: "blue",
            disabled: actionsDisabled,
          },
          {
            label: isSaving ? "Saving..." : "Submit Claim",
            onClick: () => claimFormRef.current?.submit(),
            type: "primary",
            color: "blue",
            disabled: actionsDisabled,
          },
        ]
      : [
          { label: "Cancel", onClick: onClose, color: "gray", disabled: isSaving },
          {
            label: isSaving ? "Saving..." : "Save Changes",
            onClick: () => claimFormRef.current?.saveChanges(),
            type: "primary",
            color: "blue",
            disabled: actionsDisabled,
          },
        ];

  const headerInfo = {
    "Claim Number": {
      type: "display",
      value: editingClaim?.claimNumber || (isNewClaim ? "New Claim" : "—"),
    },
    "Shipment Number": {
      type: "display",
      value: editingClaim?.shipmentNumber || "—",
    },
    "Total Claim Amount": {
      type: "display",
      value:
        editingClaim?.totalClaimAmount != null
          ? formatClaimMoney(editingClaim.totalClaimAmount)
          : "—",
    },
    Status: {
      type: "display",
      value: editingClaim?.status || (isNewClaim ? "Draft" : "—"),
    },
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <div className="sticky top-0 z-10 shrink-0 bg-white dark:bg-acs-dark-surface shadow-md w-full">
        <StickyHeader infoData={headerInfo} onInfoChange={() => {}} mode={null} buttons={headerButtons} />
      </div>

      <div className="flex-1 overflow-y-auto p-fluid-2">
        {isLoadingClaim ? (
          <div className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext font-semibold">
            Loading claim...
          </div>
        ) : loadError && !editingClaim ? (
          <div className="text-fluid-sm text-red-600 dark:text-red-400 font-semibold">{loadError}</div>
        ) : (
          <ClaimForm
            ref={claimFormRef}
            mode="standalone"
            officeCode={access.officeCodes?.join(",") || access.officeCode}
            readOnly={isReadOnlyForm}
            isSaving={isSaving}
            showStatusSection={Boolean(editingClaim)}
            /*
              LGX-1044: filing a claim used to freeze every field, which left a Shipper Admin
              with no way to correct a submitted claim. Fields are locked for view-only users
              only - `readOnly` already covers that, so nothing extra is locked here.
            */
            detailsLocked={false}
            initialValues={editingClaim ? detailsToFormValues(editingClaim) : {}}
            onCancel={onClose}
            onSubmit={handleSubmitClaim}
            fieldErrors={saveFieldErrors}
            hideFooterActions
          />
        )}
      </div>
    </div>
  );
};

export default ClaimTabContent;
